'use client';

import { useModalStore } from '@/src/shared/store/modal.store';
import { Container } from '@/src/shared/ui/Container';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import Link from 'next/link';
import { useLocale } from 'next-intl';
import { ArrowLeft } from 'lucide-react';

const clientSteps = [
  {
    number: '01',
    title: 'Post a Task',
    description:
      'Describe what you need done, set your budget, and share the details of your project.',
  },
  {
    number: '02',
    title: 'Receive Proposals',
    description:
      'Local service providers can respond to your task with their pricing, availability, and experience.',
  },
  {
    number: '03',
    title: 'Get It Done',
    description:
      'Compare profiles, reviews, and proposals to choose the right service provider for your needs.',
  },
];

const providerSteps = [
  {
    number: '01',
    title: 'Create Your Profile',
    description:
      'Create your profile, showcase your skills, set your availability, and introduce your services to potential clients.',
  },
  {
    number: '02',
    title: 'Browse & Bid',
    description:
      'Browse tasks posted in your area and send proposals that match your pricing and schedule.',
  },
  {
    number: '03',
    title: 'Grow Your Reputation',
    description:
      'Complete tasks, earn reviews, and grow your reputation to attract more clients.',
  },
];

export default function HowItWorksPage() {
  const openModal = useModalStore((s) => s.openModal);
  const locale = useLocale();

  return (
    <div className="min-h-screen bg-background-default">
      {/* Header */}
      <header className="border-b border-border-primary bg-surface-primary sticky top-0 z-50">
        <div className="container max-w-5xl mx-auto px-4 py-4">
          <Link
            href={`/${locale}`}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity w-fit"
          >
            <ArrowLeft size={20} />
            <Typography variant="bodyMd" weight="semibold">
              Back to Home
            </Typography>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-surface-primary border-b border-border-secondary py-16 md:py-24">
        <div className="container max-w-5xl mx-auto px-4 text-center">
          <Typography variant="h1" className="mb-4">
            How Hummii Works
          </Typography>
          <Typography variant="bodyLg" color="secondary" className="max-w-2xl mx-auto">
            Hummii connects people who need services with local service providers.
            Whether you&apos;re looking to get a task completed or grow your business, getting started is simple.
          </Typography>
        </div>
      </section>

      {/* For Clients */}
      <section className="py-16 md:py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="mb-10">
            <Typography variant="label" color="accent" className="uppercase tracking-widest mb-2">
              For Clients
            </Typography>
            <Typography variant="h2">Need something done?</Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            {clientSteps.map((step) => (
              <div
                key={step.number}
                className="bg-surface-primary border border-border-secondary rounded-xl p-6"
              >
                <Typography
                  variant="h1"
                  color="accent"
                  className="mb-4 opacity-30 select-none"
                >
                  {step.number}
                </Typography>
                <Typography variant="h3" className="mb-2">
                  {step.title}
                </Typography>
                <Typography variant="body" color="secondary">
                  {step.description}
                </Typography>
              </div>
            ))}
          </div>

          <PrimaryButton onClick={() => openModal('post-task')}>
            Post a Task
          </PrimaryButton>
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-border-secondary" />

      {/* For Service Providers */}
      <section className="py-16 md:py-20">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="mb-10">
            <Typography variant="label" color="accent" className="uppercase tracking-widest mb-2">
              For Service Providers
            </Typography>
            <Typography variant="h2">Ready to Grow Your Business?</Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            {providerSteps.map((step) => (
              <div
                key={step.number}
                className="bg-surface-primary border border-border-secondary rounded-xl p-6"
              >
                <Typography
                  variant="h1"
                  color="accent"
                  className="mb-4 opacity-30 select-none"
                >
                  {step.number}
                </Typography>
                <Typography variant="h3" className="mb-2">
                  {step.title}
                </Typography>
                <Typography variant="body" color="secondary">
                  {step.description}
                </Typography>
              </div>
            ))}
          </div>

          <PrimaryButton
            onClick={() => openModal('registration', { intent: { intendedRole: 'CONTRACTOR' } })}
          >
            Become a Service Provider
          </PrimaryButton>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-surface-primary border-t border-border-secondary">
        <div className="container max-w-5xl mx-auto px-4">
          <Typography variant="h2" className="mb-10">
            Frequently Asked Questions
          </Typography>
          <div className="space-y-6 max-w-3xl">
            {[
              {
                q: 'Is Hummii available in my city?',
                a: 'Hummii is currently available across major cities in Canada. Enter your postal code when posting a task to see service providers near you.',
              },
              {
                q: 'How does payment work?',
                a: 'Payment is held securely until you confirm the task is complete. Service providers are paid quickly after task completion.',
              },
              {
                q: 'What if something goes wrong?',
                a: 'Our support team is available to help resolve any disputes. Contact support through the Help section in your account.',
              },
            ].map((item) => (
              <div key={item.q} className="border-b border-border-secondary pb-6">
                <Typography variant="h4" className="mb-2">
                  {item.q}
                </Typography>
                <Typography variant="body" color="secondary">
                  {item.a}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border-secondary bg-surface-primary py-6">
        <div className="container max-w-5xl mx-auto px-4 text-center">
          <Typography variant="note" color="tertiary">
            © {new Date().getFullYear()} Hummii. All rights reserved.
          </Typography>
        </div>
      </footer>
    </div>
  );
}
